import type { RefObject } from 'react';
import { useEffect } from 'react';

export const buildStylesheets = async (
  urls: string[] = []
): Promise<CSSStyleSheet[]> => {
  const promises = Promise.allSettled(
    urls.map(async (url) => {
      const sheet = new CSSStyleSheet();
      const res = await fetch(url);
      await sheet.replace(await res.text());
      return sheet;
    })
  );

  return (await promises)
    .filter(
      (promise) =>
        /** Ignore failed fetches (from CORS errors, for example) */
        promise.status === 'fulfilled'
    )
    .map((promise) => promise.value);
};

interface ShadowStylesheetsProps {
  rootRef: RefObject<HTMLDivElement | null>;
  styleSheets: CSSStyleSheet[];
}

/**
 * External stylesheets cannot affect the Shadow DOM via typical <link> tags, but they
 * can be manually fetched and converted into CSSStyleSheet objects, which can be applied
 * to the Shadow DOM.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/ShadowrootRef/adoptedStyleSheets
 */
export const ShadowStylesheets = ({
  rootRef,
  styleSheets,
}: ShadowStylesheetsProps) => {
  useEffect(() => {
    if (rootRef.current?.shadowRoot) {
      styleSheets.forEach((sheet) => {
        rootRef.current?.shadowRoot?.adoptedStyleSheets.push(sheet);
      });
    }
  }, [rootRef, styleSheets]);

  return null;
};
